import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  DollarSign, Clock, Shield, Save, Loader2, Info, RefreshCw, Percent
} from 'lucide-react';

export default function AdminFinancialSettings() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    mode: 'manual',
    release_days: 7,
    max_auto_release_amount: 5000,
    block_on_dispute: true,
    platform_fee_percent: 25
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/financial-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.settings) {
        setSettings({
          mode: res.data.settings.mode || 'manual',
          release_days: res.data.settings.release_days || 7,
          max_auto_release_amount: res.data.settings.max_auto_release_amount || 5000,
          block_on_dispute: res.data.settings.block_on_dispute !== false,
          platform_fee_percent: res.data.settings.platform_fee_percent != null
            ? Number(res.data.settings.platform_fee_percent)
            : 25
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/financial-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao salvar configurações';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(value || 0);
  };

  if (loading) {
    return (
      <AdminLayout title="Configurações de Repasse" subtitle="Carregando...">
        <div className="space-y-6 max-w-3xl">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="financial-settings-title">
              Configurações de Repasse
            </h1>
            <p className="text-slate-500">Controle de repasses aos prestadores</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchSettings}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Alerta Informativo */}
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            Estas configurações definem as regras de repasse financeiro para prestadores. 
            A lógica automática ainda não está ativada - apenas a estrutura de configuração está disponível.
          </AlertDescription>
        </Alert>

        {/* Comissão da Plataforma (SOURCE OF TRUTH) */}
        <Card className="p-6 border-2 border-amber-100 bg-amber-50/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Percent className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Comissão da Plataforma (%)</h3>
              <p className="text-sm text-slate-500">
                Percentual retido pela JATOO sobre cada serviço prestado. Esta é a fonte oficial da divisão financeira (admin → prestador).
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={settings.platform_fee_percent}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setSettings({
                  ...settings,
                  platform_fee_percent: Number.isNaN(v) ? 0 : Math.max(0, Math.min(100, v))
                });
              }}
              className="w-32"
              data-testid="platform-fee-input"
            />
            <span className="text-slate-500">%</span>
            <span className="ml-4 text-xs text-slate-500">
              Mínimo 0% • Máximo 100% • Aceita decimais
            </span>
          </div>

          {/* Resumo visual em tempo real (sobre serviço de R$ 100 como referência) */}
          <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Bruto</p>
              <p className="font-semibold text-slate-800">R$ 100,00</p>
            </div>
            <div className="rounded-md border border-amber-200 bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-amber-700">Plataforma</p>
              <p className="font-semibold text-amber-800" data-testid="preview-platform-amount">
                R$ {Number(settings.platform_fee_percent || 0).toFixed(2).replace('.', ',')}
              </p>
            </div>
            <div className="rounded-md border border-emerald-200 bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-emerald-700">Prestador recebe</p>
              <p className="font-semibold text-emerald-800" data-testid="preview-provider-amount">
                R$ {(100 - Number(settings.platform_fee_percent || 0)).toFixed(2).replace('.', ',')}
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-3">
            Todos os cálculos financeiros (dashboards, repasses, contestações, "Você recebe") derivam deste valor em tempo real.
          </p>
        </Card>

        {/* Modo de Repasse */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Modo de Repasse</h3>
              <p className="text-sm text-slate-500">Define como os repasses serão processados</p>
            </div>
          </div>
          
          <Select
            value={settings.mode}
            onValueChange={(value) => setSettings({ ...settings, mode: value })}
          >
            <SelectTrigger className="w-full" data-testid="mode-select">
              <SelectValue placeholder="Selecione o modo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Manual</span>
                  <span className="text-xs text-slate-500">- Admin aprova cada repasse</span>
                </div>
              </SelectItem>
              <SelectItem value="automatic">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Automático</span>
                  <span className="text-xs text-slate-500">- Processado após prazo de liberação</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <p className="text-xs text-slate-400 mt-2">
            {settings.mode === 'manual' 
              ? 'No modo manual, cada repasse precisa ser aprovado individualmente pelo administrador.'
              : 'No modo automático, os repasses serão processados automaticamente após o prazo configurado.'}
          </p>
        </Card>

        {/* Prazo de Liberação */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Prazo de Liberação</h3>
              <p className="text-sm text-slate-500">Dias após conclusão do serviço para liberar o repasse</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Input
              type="number"
              min={1}
              max={30}
              value={settings.release_days}
              onChange={(e) => setSettings({ ...settings, release_days: parseInt(e.target.value) || 7 })}
              className="w-32"
              data-testid="release-days-input"
            />
            <span className="text-slate-500">dias</span>
          </div>
          
          <p className="text-xs text-slate-400 mt-2">
            Intervalo: 1 a 30 dias. Recomendado: 7 dias para permitir contestações.
          </p>
        </Card>

        {/* Limite Máximo por Repasse */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Limite Máximo por Repasse Automático</h3>
              <p className="text-sm text-slate-500">Valor máximo para repasses automáticos sem revisão</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-slate-500">R$</span>
            <Input
              type="number"
              min={100}
              max={50000}
              step={100}
              value={settings.max_auto_release_amount}
              onChange={(e) => setSettings({ ...settings, max_auto_release_amount: parseFloat(e.target.value) || 5000 })}
              className="w-40"
              data-testid="max-amount-input"
            />
          </div>
          
          <p className="text-xs text-slate-400 mt-2">
            Repasses acima deste valor exigirão aprovação manual. Intervalo: R$100 a R$50.000.
          </p>
        </Card>

        {/* Bloqueio por Disputa */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold">Bloqueio Automático por Disputa</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Quando ativo, serviços com contestações abertas terão o repasse bloqueado automaticamente
                </p>
              </div>
            </div>
            
            <Switch
              checked={settings.block_on_dispute}
              onCheckedChange={(checked) => setSettings({ ...settings, block_on_dispute: checked })}
              data-testid="block-dispute-toggle"
            />
          </div>
          
          <p className="text-xs text-slate-400 mt-4 ml-14">
            {settings.block_on_dispute 
              ? 'Repasses serão bloqueados até a resolução da disputa pelo administrador.'
              : 'Repasses serão processados normalmente mesmo com disputas abertas (não recomendado).'}
          </p>
        </Card>

        {/* Resumo */}
        <Card className="p-6 bg-slate-50">
          <h4 className="font-semibold mb-3">Resumo da Configuração</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Modo:</span>
              <span className="font-medium ml-2">
                {settings.mode === 'manual' ? 'Manual' : 'Automático'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Prazo:</span>
              <span className="font-medium ml-2">{settings.release_days} dias</span>
            </div>
            <div>
              <span className="text-slate-500">Limite automático:</span>
              <span className="font-medium ml-2">{formatCurrency(settings.max_auto_release_amount)}</span>
            </div>
            <div>
              <span className="text-slate-500">Bloqueio por disputa:</span>
              <span className={`font-medium ml-2 ${settings.block_on_dispute ? 'text-green-600' : 'text-red-600'}`}>
                {settings.block_on_dispute ? 'Ativado' : 'Desativado'}
              </span>
            </div>
          </div>
        </Card>

        {/* Botão Salvar */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            size="lg"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
